// The Research Center's insight filters (release 6.3, Kareem 5 Nov: "where are the filters?"): category (or New),
// tag, confidence and source. They combine; each choice shows how many insights it would leave given the others
// (faceted counts); they live in the URL query (cat, tag, conf, src), so a filtered view is a link. The table view
// (view=table) sorts by a column (sort, dir); by date, newest first, unless a header is clicked.
import { CONFIDENCE_LABELS, INSIGHT_CATEGORIES, INSIGHT_CONFIDENCE, type Insight, type InsightCategory, type InsightConfidence } from '@dpw/shared';

export type CatChoice = InsightCategory | 'New';
export interface InsightFilter { cat: CatChoice | null; tag: string | null; conf: InsightConfidence | null; src: string | null }
export type FilterKey = keyof InsightFilter;
export const FILTER_KEYS: FilterKey[] = ['cat', 'tag', 'conf', 'src'];
export const NO_FILTER: InsightFilter = { cat: null, tag: null, conf: null, src: null };

export type SortKey = 'finding' | 'confidence' | 'tags' | 'by' | 'date';
export const SORT_KEYS: SortKey[] = ['finding', 'confidence', 'tags', 'by', 'date'];
export type SortDir = 'asc' | 'desc';
export interface TableSort { key: SortKey; dir: SortDir }
export const DEFAULT_SORT: TableSort = { key: 'date', dir: 'desc' };

/** The filter in a URL query. Anything not recognised is ignored (an old link still opens). */
export function readFilter(p: URLSearchParams): InsightFilter {
  const cat = p.get('cat');
  const conf = p.get('conf');
  const tag = p.get('tag')?.trim().toLowerCase().replace(/^#+/, '');
  return {
    cat: cat === 'New' || (INSIGHT_CATEGORIES as readonly string[]).includes(cat ?? '') ? cat as CatChoice : null,
    tag: tag || null,
    conf: (INSIGHT_CONFIDENCE as readonly string[]).includes(conf ?? '') ? conf as InsightConfidence : null,
    src: p.get('src') || null,
  };
}

/** Set (or, with null, remove) filter values in a URL query, in place. */
export function writeFilter(p: URLSearchParams, f: Partial<InsightFilter>): URLSearchParams {
  for (const k of FILTER_KEYS) {
    if (!(k in f)) continue;
    const v = f[k];
    if (v) p.set(k, v); else p.delete(k);
  }
  return p;
}

export const isFiltered = (f: InsightFilter) => FILTER_KEYS.some(k => !!f[k]);

/** Does an insight pass the filter? except: leave that one choice out (for its own counts). */
export function passes(i: Insight, f: InsightFilter, except?: FilterKey): boolean {
  if (except !== 'cat' && f.cat && (f.cat === 'New' ? i.status !== 'new' : i.category !== f.cat)) return false;
  if (except !== 'tag' && f.tag && !i.tags.includes(f.tag)) return false;
  if (except !== 'conf' && f.conf && i.confidence !== f.conf) return false;
  if (except !== 'src' && f.src && i.sourceDomain !== f.src) return false;
  return true;
}

export const applyFilter = (all: Insight[], f: InsightFilter) => all.filter(i => passes(i, f));

export interface Facets {
  /** All, then New (for the team), then each category in use (or chosen). */
  cats: { key: CatChoice | null; label: string; n: number }[];
  /** Tags in use under the other choices, most used first (and the chosen tag, even when it leaves none). */
  tags: { tag: string; n: number }[];
  /** Each confidence the insights have (or the chosen one). */
  conf: { key: InsightConfidence; label: string; n: number }[];
  /** Source sites, most insights first. */
  sources: { domain: string; n: number }[];
}

/**
 * The choices and their counts. A choice's count is how many insights there would be with it chosen and the other
 * filters as they are, so the numbers always add up to what the list shows.
 */
export function facets(all: Insight[], f: InsightFilter, o: { team: boolean }): Facets {
  const forCat = all.filter(i => passes(i, f, 'cat'));
  const used = new Set(forCat.map(i => i.category));
  const cats: Facets['cats'] = [{ key: null, label: 'All', n: forCat.length }];
  if (o.team) cats.push({ key: 'New', label: 'New', n: forCat.filter(i => i.status === 'new').length });
  for (const c of INSIGHT_CATEGORIES) if (used.has(c) || f.cat === c || all.some(i => i.category === c)) cats.push({ key: c, label: c, n: forCat.filter(i => i.category === c).length });

  const tagCount = new Map<string, number>();
  for (const i of all.filter(x => passes(x, f, 'tag'))) for (const t of i.tags) tagCount.set(t, (tagCount.get(t) ?? 0) + 1);
  if (f.tag && !tagCount.has(f.tag)) tagCount.set(f.tag, 0);
  const tags = [...tagCount].map(([tag, n]) => ({ tag, n })).sort((a, b) => b.n - a.n || a.tag.localeCompare(b.tag));

  const forConf = all.filter(i => passes(i, f, 'conf'));
  const conf = INSIGHT_CONFIDENCE.filter(c => f.conf === c || all.some(i => i.confidence === c))
    .map(c => ({ key: c, label: CONFIDENCE_LABELS[c], n: forConf.filter(i => i.confidence === c).length }));

  const srcCount = new Map<string, number>();
  for (const i of all.filter(x => passes(x, f, 'src'))) if (i.sourceDomain) srcCount.set(i.sourceDomain, (srcCount.get(i.sourceDomain) ?? 0) + 1);
  if (f.src && !srcCount.has(f.src)) srcCount.set(f.src, 0);
  const sources = [...srcCount].map(([domain, n]) => ({ domain, n })).sort((a, b) => b.n - a.n || a.domain.localeCompare(b.domain));
  return { cats, tags, conf, sources };
}

// ---------- the table ----------

/** Who verified it, for the table: the first name, or "Unverified". */
export const verifiedBy = (i: Insight) => (i.status === 'kept' ? (i.decidedBy?.name.split(' ')[0] || 'Someone') : 'Unverified');
/** The table's date: when it was verified (kept), else when it was found. */
export const insightDate = (i: Insight) => (i.status === 'kept' && i.decidedAt ? i.decidedAt : i.createdAt);

export function readSort(p: URLSearchParams): TableSort {
  const key = p.get('sort') as SortKey | null;
  if (!key || !SORT_KEYS.includes(key)) return DEFAULT_SORT;
  return { key, dir: p.get('dir') === 'asc' ? 'asc' : p.get('dir') === 'desc' ? 'desc' : firstDir(key) };
}
/** Words sort A to Z first; the date newest first. */
export const firstDir = (k: SortKey): SortDir => (k === 'date' ? 'desc' : 'asc');
/** A header clicked: the same column turns round, another starts in its own first direction. */
export const nextSort = (cur: TableSort, key: SortKey): TableSort => (cur.key === key ? { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: firstDir(key) });

const CONF_RANK: Record<InsightConfidence, number> = { confirmed: 0, single: 1, contested: 2 };

export function sortInsights(list: Insight[], s: TableSort): Insight[] {
  const val = (i: Insight): string | number => s.key === 'finding' ? i.title.toLowerCase() : s.key === 'confidence' ? CONF_RANK[i.confidence]
    : s.key === 'tags' ? (i.tags[0] ?? '￿') : s.key === 'by' ? verifiedBy(i).toLowerCase() : Date.parse(insightDate(i));
  const sign = s.dir === 'asc' ? 1 : -1;
  // Ties: newest first, so the order never jumps about.
  return [...list].sort((a, b) => {
    const x = val(a), y = val(b);
    const c = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
    return c * sign || Date.parse(insightDate(b)) - Date.parse(insightDate(a));
  });
}
